import React, { useState, useEffect, useRef } from "react";
import Slider from "react-slick";
import { Link } from "gatsby";
import { Box } from "../elements/Box";
import { Button } from "../elements/Button";
import { Flex } from "../elements/Flex";
import { Heading } from "../elements/Heading";
import { Image } from "../elements/Image";
import { Text } from "../elements/Text";
import UKSite from "./UKSite";
import USSite from "./USSite";

import arrowLeft from "../images/icons/arrow-left.png";
import arrowRight from "../images/icons/arrow-right.png";

import Arrow from "../components/classes/MainSliderContainer/components/Arrow";

import "./HeroSection.scss";

// To use a banner across both UK & US sites pass assets as desktopImg, mobileImg or desktopVideo, mobileVideo
// To use a banner for a specific site pass assets as either ukDesktopImg, ukMobileImg or ukDesktopVideo, ukMobileVideo etc
// If you pass UK specific banner assets you must also pass US assets and vice versa, otherwise nothing will be rendered for one of the sites

// The exact same behaviour applies to the title, subtitle, cta, path and ctaDescription props and their UK/US equivalents

function Clip({ url }) {
  const videoRef = useRef();
  const previousUrl = useRef(url);

  useEffect(() => {
    if (previousUrl.current === url) {
      return;
    }

    if (videoRef.current) {
      videoRef.current.load();
    }

    previousUrl.current = url;
  }, [url]);

  return (
    <video ref={videoRef} autoPlay playsInline loop muted style={{ display: "block", width: "100%" }}>
      <source src={url} type="video/mp4" />
    </video>
  );
}

const HeroSection = ({
  title,
  subtitle,
  desktopImg,
  mobileImg,
  slideshow,
  cta,
  path,
  ctaDescription,
  ukMobileImg,
  ukDesktopImg,
  usMobileImg,
  usDesktopImg,
  ukCta,
  ukPath,
  ukCtaDescription,
  usCta,
  usPath,
  usCtaDescription,
  ukTitle,
  ukSubtitle,
  usTitle,
  usSubtitle,
  mobileVideo,
  desktopVideo,
  ukMobileVideo,
  ukDesktopVideo,
  usMobileVideo,
  usDesktopVideo,
  children,
  videoSlideShow,
}) => {
  const [slideIndex, setSlideIndex] = useState(0);

  const [srcVideo, setSrcVideo] = useState(videoSlideShow ? videoSlideShow[0] : null);

  const changeSlide = (direction) => {
    let index = slideIndex;
    if (direction == "left") {
      index--;
    } else {
      index++;
      index %= videoSlideShow.length;
    }

    if (index < 0) {
      index = videoSlideShow.length - 1;
    }

    console.log(slideIndex, srcVideo, "changeSlide");
    setSlideIndex(index);
    setSrcVideo(videoSlideShow[index]);
  };

  return (
    <Box position="relative" width="100%" bg="pink">
      <Flex
        position="absolute"
        top={0}
        height="100%"
        width="100%"
        p={3}
        zIndex={10}
        justifyContent="center"
        alignItems="center"
      >
        <Flex
          position="relative"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          textAlign="center"
          maxWidth="1000px"
          height="100%"
          margin="0 auto"
        >
          {title && (
            <Heading stroke fontSize={[35, 40, 40, 55]}>
              {title}
            </Heading>
          )}

          {subtitle && (
            <Text my={3} color="white" fontWeight={600}>
              {subtitle}
            </Text>
          )}

          <UKSite>
            {ukTitle && (
              <Heading stroke fontSize={[35, 40, 40, 55]}>
                {ukTitle}
              </Heading>
            )}

            {ukSubtitle && (
              <Text my={3} color="white" fontWeight={600}>
                {ukSubtitle}
              </Text>
            )}
          </UKSite>

          <USSite>
            {usTitle && (
              <Heading stroke fontSize={[35, 40, 40, 55]}>
                {usTitle}
              </Heading>
            )}

            {usSubtitle && (
              <Text my={3} color="white" fontWeight={600}>
                {usSubtitle}
              </Text>
            )}
          </USSite>

          {children}

          {cta && (
            <Flex flexDirection="column" width="100%" minWidth="300px" position="absolute" zIndex={50} bottom={0}>
              <Link to={path}>
                <Button variant="rounded" margin="0 auto" bg="darkPink" color="white">
                  {cta}
                </Button>
              </Link>

              <Text color="white" fontWeight={600} mt={2} style={{ fontSize: "12px" }}>
                {ctaDescription}
              </Text>
            </Flex>
          )}

          {ukCta && (
            <UKSite>
              <Flex flexDirection="column" width="100%" minWidth="300px" zIndex={50} bottom={0}>
                <Link to={ukPath}>
                  <Button variant="rounded" margin="0 auto" bg="darkPink" color="white">
                    {ukCta}
                  </Button>
                </Link>

                <Text color="white" fontWeight={600} mt={2} style={{ fontSize: "12px" }}>
                  {ukCtaDescription}
                </Text>
              </Flex>
            </UKSite>
          )}

          {usCta && (
            <USSite>
              <Box Flex flexDirection="column" width="100%" minWidth="300px" zIndex={50} bottom={0}>
                <Link to={usPath}>
                  <Button variant="rounded" margin="0 auto" bg="darkPink" color="white">
                    {usCta}
                  </Button>
                </Link>

                <Text color="white" fontWeight={600} mt={2} style={{ fontSize: "12px" }}>
                  {usCtaDescription}
                </Text>
              </Box>
            </USSite>
          )}
        </Flex>
      </Flex>

      {slideshow && (
        <>
          <div className="desktop-slider">
            <Slider
              {...{
                slidesToScroll: 1,
                arrows: true,
                autoplay: false,
                centerMode: true,
                infinite: true,
                speed: 300,
                centerPadding: "1px",
                prevArrow: <Arrow left color="white" />,
                nextArrow: <Arrow right color="white" />,
              }}
            >
              {slideshow.desktop.map((item) =>
                item.type === "video" ? (
                  <Link to={item.path || "/"}>
                    <video autoPlay playsInline loop muted style={{ display: "block", width: "100%" }}>
                      <source type="video/mp4" src={item.src} />
                    </video>
                  </Link>
                ) : (
                  <Link to={item.path || "/"}>
                    <div style={{ position: "relative" }} key={item}>
                      <div className="gradient"></div>
                      <Image height="100%" width="100%" src={item} />
                    </div>
                  </Link>
                )
              )}
            </Slider>
          </div>

          <div className="mobile-slider">
            <Slider
              {...{
                slidesToScroll: 1,

                arrows: false,
                autoplay: false,
                centerMode: true,
                infinite: true,
                speed: 300,
                centerPadding: "1px",
                prevArrow: <Arrow left color="white" />,
                nextArrow: <Arrow righ color="white" t />,
              }}
            >
              {slideshow.mobile.map((item) =>
                item.type === "video" ? (
                  <video autoPlay playsInline loop muted style={{ display: "block", width: "100%" }}>
                    <source type="video/mp4" src={item.src} />
                  </video>
                ) : (
                  <div style={{ position: "relative" }} key={item}>
                    <div className="gradient"></div>
                    <Image width="100%" src={item} />
                  </div>
                )
              )}
            </Slider>
          </div>

          {/* <Image
            display={["block", "block", "none"]}
            width="100%"
            height="auto"
            maxHeight="80vh"
            objectFit="cover"
            src={mobileImg}
          /> */}
        </>
      )}

      {mobileImg && desktopImg && (
        <>
          <Image
            display={["none", "none", "block"]}
            width="100%"
            height="auto"
            maxHeight="80vh"
            objectFit="cover"
            src={desktopImg}
          />
          <Image
            display={["block", "block", "none"]}
            width="100%"
            height="auto"
            maxHeight="80vh"
            objectFit="cover"
            src={mobileImg}
          />
        </>
      )}

      {ukDesktopImg && ukMobileImg && (
        <UKSite>
          <Image
            display={["none", "none", "block"]}
            width="100%"
            height="auto"
            maxHeight="80vh"
            objectFit="cover"
            src={ukDesktopImg}
          />

          <Image
            display={["block", "block", "none"]}
            width="100%"
            height="auto"
            maxHeight="80vh"
            objectFit="cover"
            src={ukMobileImg}
          />
        </UKSite>
      )}

      {usDesktopImg && usMobileImg && (
        <USSite>
          <Image
            display={["none", "none", "block"]}
            width="100%"
            height="auto"
            maxHeight="80vh"
            objectFit="cover"
            src={usDesktopImg}
          />

          <Image
            display={["block", "block", "none"]}
            width="100%"
            height="auto"
            maxHeight="80vh"
            objectFit="cover"
            src={usMobileImg}
          />
        </USSite>
      )}

      {videoSlideShow && videoSlideShow.length && (
        <>
          {" "}
          <img src={arrowLeft} onClick={() => changeSlide("left")} className="arrow-left" />
          <img src={arrowRight} onClick={() => changeSlide("right")} className="arrow-right" />
          <Link to={path || ukPath || usPath || null}>
            <Box className="box-video">
              <Clip url={srcVideo} />
            </Box>
          </Link>
        </>
      )}

      {mobileVideo && desktopVideo && (
        <>
          <Box display={["none", "none", "block"]}>
            <video autoPlay playsInline loop muted style={{ display: "block", width: "100%" }}>
              <source src={desktopVideo} type="video/mp4" />
            </video>
          </Box>
          <Box display={["block", "block", "none"]}>
            <video autoPlay playsInline loop muted style={{ display: "block", width: "100%" }}>
              <source src={mobileVideo} type="video/mp4" />
            </video>
          </Box>
        </>
      )}

      {ukMobileVideo && ukDesktopVideo && (
        <>
          <Box display={["none", "none", "block"]}>
            <video autoPlay playsInline loop muted style={{ display: "block", width: "100%" }}>
              <source src={ukDesktopVideo} type="video/mp4" />
            </video>
          </Box>
          <Box display={["block", "block", "none"]}>
            <video autoPlay playsInline loop muted style={{ display: "block", width: "100%" }}>
              <source src={ukMobileVideo} type="video/mp4" />
            </video>
          </Box>
        </>
      )}

      {usMobileVideo && usDesktopVideo && (
        <>
          <Box display={["none", "none", "block"]}>
            <video autoPlay playsInline loop muted style={{ display: "block", width: "100%" }}>
              <source src={usDesktopVideo} type="video/mp4" />
            </video>
          </Box>
          <Box display={["block", "block", "none"]}>
            <video autoPlay playsInline loop muted style={{ display: "block", width: "100%" }}>
              <source src={usMobileVideo} type="video/mp4" />
            </video>
          </Box>
        </>
      )}
    </Box>
  );
};

export default HeroSection;
